import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ControlledEditor from '@monaco-editor/react';
import AlertValidationPending from './AlertValidationPending';
import AlertInvalidJson from './AlertInvalidJson';

function debounce<T extends (...args: Parameters<T>) => void>(fn: T, ms: number) {
  let id: ReturnType<typeof setTimeout>;
  const debounced = (...args: Parameters<T>) => {
    clearTimeout(id);
    id = setTimeout(() => fn(...args), ms);
  };
  debounced.cancel = () => clearTimeout(id);
  return debounced;
}

interface IProps {
  formData: Record<string, unknown>;
  setFormData: (formData: Record<string, unknown>) => void;
}

export default function JsonEditor(props: IProps) {
  const { formData, setFormData } = props;

  const initValue = useMemo(() => JSON.stringify(formData, null, 2), [formData]);

  const [validationStatus, setValidationStatus] = useState<'PENDING' | 'NOT_VALIDATED' | 'VALIDATED'>('VALIDATED');

  const onValueChanged = useCallback(
    (value: string) => {
      try {
        const formData = JSON.parse(value);
        setValidationStatus('VALIDATED');
        setFormData(formData);
      } catch (error) {
        setValidationStatus('NOT_VALIDATED');
        console.error('Could not parse:', error);
      }
    },
    [setFormData],
  );
  const debouncedOnValueChange = useMemo(() => debounce(onValueChanged, 300), [onValueChanged]);

  useEffect(() => {
    return () => debouncedOnValueChange.cancel();
  }, [debouncedOnValueChange]);

  const getValueRef = useRef<() => string>(null);
  useEffect(() => {
    // Store the current value of getValueRef.current at the time the effect runs
    const currentGetValue = getValueRef.current;
    return () => {
      if (!currentGetValue) {
        return;
      }
      const value = currentGetValue();
      onValueChanged(value);
    };
  }, [onValueChanged]);

  return (
    <>
      {(() => {
        switch (validationStatus) {
          case 'PENDING':
            return <AlertValidationPending />;
          case 'NOT_VALIDATED':
            return <AlertInvalidJson />;
        }
      })()}

      <div style={{ height: '48px' }} />

      <ControlledEditor
        height="400px"
        theme="vs-light"
        language="json"
        options={{
          formatOnType: true,
          formatOnPaste: true,
        }}
        value={initValue}
        onChange={(value: string | void) => {
          setValidationStatus('PENDING');
          debouncedOnValueChange(value || '');
        }}
        loading={'Loading...'}
        onMount={(editor) => {
          getValueRef.current = () => editor.getValue();
        }}
      />
    </>
  );
}
